import React, { useState, useEffect, useCallback } from 'react';
import {
  IconChevronLeft, IconChevronRight, IconPlus, IconEdit, IconTrash, IconX, IconCalendarEvent, IconGolf, IconFlag,
  IconSun, IconCloud, IconCloudRain, IconSnowflake, IconWind, IconCloudStorm,
} from '@tabler/icons-react';
import api from '../api/api';
import BookingFormModal from '../components/BookingFormModal';
import { onBackdropMouseDown, onBackdropClick } from '../utils/modalDrag';

const WEATHER_MAP = {
  sunny:  { Icon: IconSun,        color: '#f59e0b', label: '맑음' },
  cloudy: { Icon: IconCloud,      color: '#6b7280', label: '흐림' },
  rain:   { Icon: IconCloudRain,  color: '#3b82f6', label: '비' },
  snow:   { Icon: IconSnowflake,  color: '#60a5fa', label: '눈' },
  wind:   { Icon: IconWind,       color: '#8b5cf6', label: '바람' },
  storm:  { Icon: IconCloudStorm, color: '#ef4444', label: '폭풍' },
};

const DAYS = ['일', '월', '화', '수', '목', '금', '토'];
const COLORS = ['#206bc4', '#2fb344', '#d63939', '#f76707', '#ae3ec9', '#0ca678', '#d6336c'];

export default function SchedulePage({ me, showToast }) {
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [schedules, setSchedules] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false); // false=create, true=edit
  const [bookingModal, setBookingModal] = useState(false);
  const [editBooking, setEditBooking] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [form, setForm] = useState({ title: '', description: '', startDate: '', endDate: '' });

  const fetchSchedules = useCallback(async (y, m) => {
    try {
      const res = await api.get(`/schedules?year=${y}&month=${m}`);
      setSchedules(res.data);
    } catch { setSchedules([]); }
  }, []);

  const fetchBookings = useCallback(async (y, m) => {
    try {
      const dateFrom = `${y}-${String(m).padStart(2, '0')}-01`;
      const dateTo = `${y}-${String(m).padStart(2, '0')}-${new Date(y, m, 0).getDate()}`;
      const res = await api.get('/admin/golf/reservations', {
        params: { dateFrom, dateTo, size: 100, sortField: 'reservedTime', sortDir: 'asc' },
      });
      setBookings(res.data.content || []);
    } catch { setBookings([]); }
  }, []);

  useEffect(() => { fetchSchedules(year, month); fetchBookings(year, month); }, [year, month, fetchSchedules, fetchBookings]);

  /* ── 달력 그리드 생성 ── */
  const buildCalendar = () => {
    const firstDay = new Date(year, month - 1, 1).getDay();
    const daysInMonth = new Date(year, month, 0).getDate();
    const rows = [];
    let cells = [];

    // 이전 달 빈칸
    for (let i = 0; i < firstDay; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
    // 나머지 빈칸
    while (cells.length % 7 !== 0) cells.push(null);

    for (let i = 0; i < cells.length; i += 7) {
      rows.push(cells.slice(i, i + 7));
    }
    return rows;
  };

  /* ── 특정 날짜에 걸치는 일정 필터 ── */
  const getSchedulesForDate = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return schedules.filter(s => s.startDate <= dateStr && s.endDate >= dateStr);
  };

  const getBookingsForDate = (day) => {
    if (!day) return [];
    const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return bookings.filter(b => b.reservedDate === dateStr);
  };

  /* ── 월 이동 ── */
  const prevMonth = () => {
    if (month === 1) { setYear(year - 1); setMonth(12); }
    else setMonth(month - 1);
    setSelectedDate(null);
  };
  const nextMonth = () => {
    if (month === 12) { setYear(year + 1); setMonth(1); }
    else setMonth(month + 1);
    setSelectedDate(null);
  };

  /* ── 모달 열기 ── */
  const openCreateModal = (day) => {
    const dateStr = day
      ? `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
      : new Date().toLocaleDateString('sv');
    setForm({ title: '', description: '', startDate: dateStr, endDate: dateStr });
    setEditMode(false);
    setSelectedSchedule(null);
    setModalOpen(true);
  };
  const openEditModal = (schedule) => {
    setForm({
      title: schedule.title,
      description: schedule.description || '',
      startDate: schedule.startDate,
      endDate: schedule.endDate,
    });
    setSelectedSchedule(schedule);
    setEditMode(true);
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setSelectedSchedule(null); };

  /* ── 저장 ── */
  const handleSave = async () => {
    if (!form.title.trim()) { showToast('제목을 입력해주세요.', 'danger'); return; }
    if (!form.startDate || !form.endDate) { showToast('날짜를 입력해주세요.', 'danger'); return; }
    if (form.endDate < form.startDate) { showToast('종료일은 시작일 이후여야 합니다.', 'danger'); return; }

    try {
      if (editMode && selectedSchedule) {
        await api.put(`/schedules/${selectedSchedule.id}`, form);
        showToast('일정이 수정되었습니다.');
      } else {
        await api.post('/schedules', form);
        showToast('일정이 등록되었습니다.');
      }
      closeModal();
      fetchSchedules(year, month);
    } catch {
      showToast('일정 저장에 실패했습니다.', 'danger');
    }
  };

  /* ── 삭제 ── */
  const handleDelete = async (id) => {
    if (!window.confirm('일정을 삭제하시겠습니까?')) return;
    try {
      await api.delete(`/schedules/${id}`);
      showToast('일정이 삭제되었습니다.');
      setSelectedSchedule(null);
      fetchSchedules(year, month);
    } catch {
      showToast('삭제에 실패했습니다.', 'danger');
    }
  };

  const todayStr = new Date().toLocaleDateString('sv');
  const todayDay = now.getFullYear() === year && now.getMonth() + 1 === month ? now.getDate() : null;
  const rows = buildCalendar();

  return (
    <div className="page-body">
      <div className="container-xl">
        <div className="page-header d-flex align-items-center mb-3">
          <h2 className="page-title">
            <IconCalendarEvent size={24} className="me-2" />
            일정관리
          </h2>
          <div className="ms-auto d-flex gap-2">
            <button className="btn btn-sm btn-success" onClick={() => setBookingModal(true)}>
              <IconGolf size={16} className="me-1" />부킹 추가
            </button>
            <button className="btn btn-sm btn-primary" onClick={() => openCreateModal(null)}>
              <IconPlus size={16} className="me-1" />일정 등록
            </button>
          </div>
        </div>

        <div className="row g-3">
          {/* ── 달력 ── */}
          <div className="col-lg-8">
            <div className="card">
              <div className="card-header d-flex align-items-center">
                <button className="btn btn-ghost-secondary btn-sm px-2" onClick={prevMonth}>
                  <IconChevronLeft size={18} />
                </button>
                <h3 className="card-title mb-0 mx-3" style={{ minWidth: 120, textAlign: 'center' }}>
                  {year}년 {month}월
                </h3>
                <button className="btn btn-ghost-secondary btn-sm px-2" onClick={nextMonth}>
                  <IconChevronRight size={18} />
                </button>
              </div>
              <div className="card-body p-0">
                <table className="table table-bordered mb-0" style={{ tableLayout: 'fixed' }}>
                  <thead>
                    <tr>
                      {DAYS.map((d, i) => (
                        <th key={d} className="text-center py-2" style={{
                          fontSize: 13, fontWeight: 600,
                          color: i === 0 ? '#d63939' : i === 6 ? '#206bc4' : 'inherit',
                          background: 'var(--tblr-bg-surface-secondary, #f8f9fa)',
                        }}>{d}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row, ri) => (
                      <tr key={ri}>
                        {row.map((day, ci) => {
                          const daySchedules = getSchedulesForDate(day);
                          const dayBookings = getBookingsForDate(day);
                          const allItems = [
                            ...daySchedules.map(s => ({ type: 'schedule', data: s })),
                            ...dayBookings.map(b => ({ type: 'booking', data: b })),
                          ];
                          const isToday = day === todayDay;
                          const isSelected = day === selectedDate;
                          return (
                            <td key={ci}
                              style={{
                                height: 90, verticalAlign: 'top', padding: '4px 6px',
                                cursor: day ? 'pointer' : 'default',
                                background: !day ? 'var(--tblr-bg-surface-secondary, #f8f9fa)' : isSelected ? 'rgba(32,107,196,0.12)' : isToday ? 'rgba(255,252,230,0.3)' : 'transparent',
                              }}
                              onClick={() => day && setSelectedDate(day)}
                              onDoubleClick={() => day && openCreateModal(day)}
                            >
                              {day && (
                                <>
                                  <div style={{
                                    fontSize: 13, fontWeight: isToday ? 700 : 400,
                                    color: ci === 0 ? '#d63939' : ci === 6 ? '#206bc4' : 'inherit',
                                    marginBottom: 2,
                                  }}>
                                    {isToday ? (
                                      <span style={{
                                        background: '#206bc4', color: '#fff', borderRadius: '50%',
                                        width: 22, height: 22, display: 'inline-flex',
                                        alignItems: 'center', justifyContent: 'center', fontSize: 12,
                                      }}>{day}</span>
                                    ) : day}
                                  </div>
                                  {allItems.slice(0, 3).map((item, idx) => (
                                    item.type === 'schedule' ? (
                                      <div key={`s-${item.data.id}`}
                                        style={{
                                          fontSize: 11, padding: '1px 4px', marginBottom: 1,
                                          borderRadius: 3, color: '#fff',
                                          background: COLORS[item.data.id % COLORS.length],
                                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                          cursor: 'pointer',
                                        }}
                                        onClick={(e) => { e.stopPropagation(); openEditModal(item.data); }}
                                      >
                                        {item.data.title}
                                      </div>
                                    ) : (
                                      <div key={`b-${item.data.id}`}
                                        style={{
                                          fontSize: 11, padding: '1px 4px', marginBottom: 1,
                                          borderRadius: 3, display: 'flex', alignItems: 'center', gap: 2,
                                          color: item.data.gameType === '메이저' ? '#ae3ec9' : '#2fb344',
                                          background: item.data.gameType === '메이저' ? '#f3e8ff' : '#e6f9ed',
                                          border: `1px solid ${item.data.gameType === '메이저' ? '#d8b4fe' : '#c3e6cb'}`,
                                          overflow: 'hidden', whiteSpace: 'nowrap',
                                        }}
                                      >
                                        <IconFlag size={9} style={{ flexShrink: 0 }} />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.data.reservedTime?.slice(0, 5)} {item.data.golfCourseName}</span>
                                      </div>
                                    )
                                  ))}
                                  {allItems.length > 3 && (
                                    <div style={{ fontSize: 10, color: '#888' }}>+{allItems.length - 3}건</div>
                                  )}
                                </>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* ── 선택 날짜 일정 목록 ── */}
          <div className="col-lg-4">
            <div className="card h-100">
              <div className="card-header">
                <h3 className="card-title mb-0">
                  {selectedDate
                    ? `${month}/${selectedDate} 일정`
                    : '날짜를 선택하세요'}
                </h3>
                {selectedDate && (
                  <div className="ms-auto">
                    <button className="btn btn-primary btn-sm" onClick={() => openCreateModal(selectedDate)}>
                      <IconPlus size={14} className="me-1" />추가
                    </button>
                  </div>
                )}
              </div>
              <div className="card-body" style={{ overflowY: 'auto' }}>
                {!selectedDate ? (
                  <div className="text-muted text-center py-4">달력에서 날짜를 클릭하세요</div>
                ) : (() => {
                  const dayItems = getSchedulesForDate(selectedDate);
                  const dayBkgs = getBookingsForDate(selectedDate);
                  if (dayItems.length === 0 && dayBkgs.length === 0) return (
                    <div className="text-muted text-center py-4">일정이 없습니다</div>
                  );
                  return (
                    <>
                      {dayBkgs.map(b => {
                        const isMajor = b.gameType === '메이저';
                        return (
                          <div key={`b-${b.id}`} className="border rounded p-3 mb-2"
                            style={{ borderLeft: `3px solid ${isMajor ? '#ae3ec9' : '#2fb344'}` }}>
                            <div className="d-flex align-items-start justify-content-between">
                              <div>
                                <div className="d-flex align-items-center gap-2 mb-1">
                                  <IconGolf size={14} color={isMajor ? '#ae3ec9' : '#2fb344'} />
                                  <span className="fw-semibold" style={{ fontSize: 14 }}>{b.golfCourseName}</span>
                                  <span className={`badge ${isMajor ? 'bg-purple-lt' : 'bg-green-lt'}`} style={{ fontSize: 10 }}>{b.gameType || '부킹'}</span>
                                </div>
                                <div className="text-muted small">
                                  {b.reservedTime?.slice(0, 5)} 티오프
                                  {b.sections?.length > 0 && ` · ${b.sections.map(s => s.sectionName).join(' > ')}`}
                                  {b.totalHoles && ` · ${b.totalHoles}홀`}
                                </div>
                                {b.greenFee != null && (
                                  <div className="text-muted small">그린피 {b.greenFee}</div>
                                )}
                                <div className="text-muted" style={{ fontSize: 11, marginTop: 4 }}>
                                  {b.playerCount}명
                                  {b.reserverName && ` · ${b.reserverName}`}
                                  {(b.weather || b.temperature) && (
                                    <span className="ms-2">
                                      {b.weather && (() => { const w = WEATHER_MAP[b.weather]; return w ? <w.Icon size={11} color={w.color} style={{ verticalAlign: '-1px' }} /> : null; })()}
                                      {b.temperature && <span className="ms-1">{b.temperature}°</span>}
                                    </span>
                                  )}
                                </div>
                                {b.memo && (
                                  <div className="text-muted" style={{ fontSize: 11, marginTop: 4, whiteSpace: 'pre-wrap' }}>
                                    💬 {b.memo}
                                  </div>
                                )}
                                {b.adminMemo && (
                                  <div style={{ fontSize: 11, marginTop: 2, color: '#d6336c', whiteSpace: 'pre-wrap' }}>
                                    🔒 {b.adminMemo}
                                  </div>
                                )}
                              </div>
                              <button className="btn btn-ghost-primary btn-sm px-1 flex-shrink-0"
                                onClick={() => { setEditBooking(b); setBookingModal(true); }}
                                title="수정">
                                <IconEdit size={15} />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                      {dayItems.map(s => (
                        <div key={s.id} className="border rounded p-3 mb-2">
                          <div className="d-flex align-items-start justify-content-between">
                            <div>
                              <div style={{
                                fontWeight: 600, fontSize: 14,
                                borderLeft: `3px solid ${COLORS[s.id % COLORS.length]}`,
                                paddingLeft: 8,
                              }}>
                                {s.title}
                              </div>
                              <div className="text-muted small mt-1">
                                {s.startDate === s.endDate ? s.startDate : `${s.startDate} ~ ${s.endDate}`}
                              </div>
                              {s.description && (
                                <div className="text-muted small mt-2" style={{ whiteSpace: 'pre-wrap' }}>
                                  {s.description}
                                </div>
                              )}
                              <div className="text-muted" style={{ fontSize: 11, marginTop: 6 }}>
                                등록: {s.createdByName}
                              </div>
                            </div>
                            <div className="d-flex gap-1 flex-shrink-0">
                              <button className="btn btn-ghost-primary btn-sm px-1" onClick={() => openEditModal(s)}>
                                <IconEdit size={15} />
                              </button>
                              <button className="btn btn-ghost-danger btn-sm px-1" onClick={() => handleDelete(s.id)}>
                                <IconTrash size={15} />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>

        {/* ── 등록/수정 모달 ── */}
        {modalOpen && (
          <>
            <div className="modal-backdrop fade show" onMouseDown={onBackdropMouseDown} onClick={(e) => onBackdropClick(e, closeModal)} />
            <div className="modal modal-blur fade show" style={{ display: 'block' }} tabIndex={-1}>
              <div className="modal-dialog modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">{editMode ? '일정 수정' : '일정 등록'}</h5>
                    <button className="btn-close" onClick={closeModal} />
                  </div>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">제목 <span className="text-danger">*</span></label>
                      <input className="form-control form-control-sm" value={form.title}
                        onChange={e => setForm({ ...form, title: e.target.value })}
                        placeholder="일정 제목" maxLength={200} autoFocus />
                    </div>
                    <div className="row mb-3">
                      <div className="col-6">
                        <label className="form-label">시작일 <span className="text-danger">*</span></label>
                        <input type="date" className="form-control form-control-sm" value={form.startDate}
                          onChange={e => setForm({ ...form, startDate: e.target.value })} />
                      </div>
                      <div className="col-6">
                        <label className="form-label">종료일 <span className="text-danger">*</span></label>
                        <input type="date" className="form-control form-control-sm" value={form.endDate}
                          onChange={e => setForm({ ...form, endDate: e.target.value })} />
                      </div>
                    </div>
                    <div className="mb-3">
                      <label className="form-label">설명</label>
                      <textarea className="form-control form-control-sm" rows={4} value={form.description}
                        onChange={e => setForm({ ...form, description: e.target.value })}
                        placeholder="일정에 대한 설명 (선택사항)" />
                    </div>
                  </div>
                  <div className="modal-footer">
                    {editMode && selectedSchedule && (
                      <button className="btn btn-sm btn-danger me-auto" onClick={() => { handleDelete(selectedSchedule.id); closeModal(); }}>
                        <IconTrash size={16} className="me-1" />삭제
                      </button>
                    )}
                    <button className="btn btn-sm" onClick={closeModal}>취소</button>
                    <button className="btn btn-sm btn-primary" onClick={handleSave}>
                      {editMode ? '수정' : '등록'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {bookingModal && (
          <BookingFormModal
            onClose={() => { setBookingModal(false); setEditBooking(null); }}
            onSaved={() => fetchBookings(year, month)}
            showToast={showToast}
            apiPrefix="/admin/golf"
            editData={editBooking}
          />
        )}
      </div>
    </div>
  );
}
